// web-frontend/src/shared/services/socialAuthService.js - 웹용 (더미 구현)

/**
 * 🔥 웹용 소셜 인증 서비스 (비활성화)
 * - OAuth는 웹 환경에서 별도 구현 필요
 * - 현재는 더미 구현으로 제공
 */

import { OAUTH_CONFIG } from '../constants/config';
import logger from '../utils/logger';

class SocialAuthService {
  constructor() {
    this.initialized = false;
    logger.debug('📱 Social Auth Service (웹용 - 비활성화)');
  }

  /**
   * 초기화 (웹에서는 불필요)
   */
  async initialize() {
    if (this.initialized) {
      return;
    }

    logger.debug('🔐 소셜 로그인 초기화 (웹 환경 - 비활성화)');
    this.initialized = true;
  }

  /**
   * 소셜 로그인 (웹에서는 비활성화)
   */
  async signInWithProvider(provider) {
    logger.warn(`⚠️ ${provider} 소셜 로그인은 웹 환경에서 비활성화되어 있습니다.`);

    // OAuth가 활성화되지 않았음을 알림
    throw new Error(`소셜 로그인은 현재 웹 환경에서 지원되지 않습니다. 일반 로그인을 사용해주세요.`);
  }

  /**
   * Google 로그인 (웹 구현 필요)
   */
  async signInWithGoogle() {
    if (!OAUTH_CONFIG.GOOGLE.ENABLED) {
      throw new Error('Google 로그인이 비활성화되어 있습니다.');
    }

    logger.warn('⚠️ Google 로그인은 웹 환경에서 별도 구현이 필요합니다.');
    throw new Error('Google 로그인은 현재 지원되지 않습니다.');

    // TODO: 웹용 Google OAuth 구현
    // - Google Identity Services 사용
    // - https://developers.google.com/identity/gsi/web
  }

  /**
   * Apple 로그인 (웹 구현 필요)
   */
  async signInWithApple() {
    if (!OAUTH_CONFIG.APPLE.ENABLED) {
      throw new Error('Apple 로그인이 비활성화되어 있습니다.');
    }

    logger.warn('⚠️ Apple 로그인은 웹 환경에서 별도 구현이 필요합니다.');
    throw new Error('Apple 로그인은 현재 지원되지 않습니다.');

    // TODO: 웹용 Apple Sign In 구현
    // - Apple JS SDK 사용
    // - https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js
  }

  /**
   * Kakao 로그인 (웹 구현 필요)
   */
  async signInWithKakao() {
    if (!OAUTH_CONFIG.KAKAO.ENABLED) {
      throw new Error('Kakao 로그인이 비활성화되어 있습니다.');
    }

    logger.warn('⚠️ Kakao 로그인은 웹 환경에서 별도 구현이 필요합니다.');
    throw new Error('Kakao 로그인은 현재 지원되지 않습니다.');

    // TODO: 웹용 Kakao Login 구현
    // - Kakao JS SDK 사용
    // - https://developers.kakao.com/docs/latest/ko/kakaologin/js
  }

  /**
   * Naver 로그인 (웹 구현 필요)
   */
  async signInWithNaver() {
    if (!OAUTH_CONFIG.NAVER.ENABLED) {
      throw new Error('Naver 로그인이 비활성화되어 있습니다.');
    }

    logger.warn('⚠️ Naver 로그인은 웹 환경에서 별도 구현이 필요합니다.');
    throw new Error('Naver 로그인은 현재 지원되지 않습니다.');

    // TODO: 웹용 Naver Login 구현
    // - Naver Login API 사용
    // - https://developers.naver.com/docs/login/web/web.md
  }

  /**
   * 로그아웃 (웹에서는 불필요)
   */
  async signOut() {
    logger.debug('🚪 소셜 로그아웃 (웹 환경 - 불필요)');
    // 웹에서는 일반 로그아웃만 처리
  }

  /**
   * OAuth 설정 확인
   */
  isConfigured(provider) {
    if (!OAUTH_CONFIG.ENABLED) {
      return false;
    }

    switch (provider?.toLowerCase()) {
      case 'google':
        return OAUTH_CONFIG.GOOGLE.ENABLED;
      case 'apple':
        return OAUTH_CONFIG.APPLE.ENABLED;
      case 'kakao':
        return OAUTH_CONFIG.KAKAO.ENABLED;
      case 'naver':
        return OAUTH_CONFIG.NAVER.ENABLED;
      default:
        return false;
    }
  }

  /**
   * 지원 가능한 Provider 목록
   */
  getAvailableProviders() {
    if (!OAUTH_CONFIG.ENABLED) {
      return [];
    }

    const providers = [];

    if (OAUTH_CONFIG.GOOGLE.ENABLED) providers.push('google');
    if (OAUTH_CONFIG.APPLE.ENABLED) providers.push('apple');
    if (OAUTH_CONFIG.KAKAO.ENABLED) providers.push('kakao');
    if (OAUTH_CONFIG.NAVER.ENABLED) providers.push('naver');

    return providers;
  }

  /**
   * 서비스 정보
   */
  getServiceInfo() {
    return {
      platform: 'web',
      oauthEnabled: OAUTH_CONFIG.ENABLED,
      availableProviders: this.getAvailableProviders(),
      note: 'OAuth는 웹 환경에서 별도 구현이 필요합니다.',
    };
  }
}

// 싱글톤 인스턴스
const socialAuthService = new SocialAuthService();

// 개발 모드에서 서비스 정보 출력
if (import.meta.env.DEV) {
  setTimeout(() => {
    logger.debug('📱 Social Auth Service Info:', socialAuthService.getServiceInfo());
  }, 1000);
}

export default socialAuthService;